import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { docEntityTypeToChildTypes } from "building-types/doc/docEntityTypeToForeignKeyRelations";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Schema } from "common/data/schema";
import { arrayShallowEquals } from "common/utils/arrays";
import { assert, assertIsNotUndefined } from "common/utils/assertOfType";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import {
  docGetSourceAssociatedEntities,
  docGetTargetAssociatedEntities,
} from "../association/docEntityGetAssociations";
import { extractDocAssociationTableReferences } from "../docAssociationTableTools";
import { docGetEntityName } from "../docGetEntityName";
import type { DocTableFieldNames } from "../docTableFieldNames";
import { docGetSchemaForEntity } from "../schema/docSchemaTools";
import { docEntityHandleHash } from "./core/handleHash";
import type { DocEntityConfig } from "./docEntityConfig";
import { docEntityGetConfig } from "./docEntityConfigTools";
import { getDocEntityReverseReferenceCandidateTypes } from "./docEntityConstraintTools";
import { docEntityGetChildrenOfType } from "./docEntityForEachChild";
import docEntityGetChildren, { docEntityGetChildrenIgnoringGroups } from "./docEntityGetChildren";
import docEntityGetParent from "./docEntityGetParent";
import type {
  DocEntityNode,
  DocEntityNodeConfigFilterCriteria,
  DocEntityNodeGetAllDocRefDependenciesAsTreeOptions,
  DocEntityNodeGetAllDependenciesOptions,
  DocEntityNodeTree,
  DocEntityNodeTraverseDependenciesCriteria,
} from "./docEntityNode";
import { getDocRefTraversalReferenceNodes } from "./docEntityNodeReferenceFiltering";
import { validateDocEntityNodeReferences } from "./docEntityNodeSchemaUtils";
import type { DocEntityReference } from "./docEntityReferenceTypes";
import { docEntityTypePrettyName } from "./docEntityTypePrettyName";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import type { DocEntityTypeToHandleType } from "./docEntityTypeToHandleType";
import type { DocEntityTypeToParentHandleType } from "./docEntityTypeToParentHandleType";
import type { DocEntityChildrenEntityType } from "./docEntityTypeUtils";
import { docForEachEntityHandle } from "./docForEachEntity";
import docGetEntity from "./docGetEntity";
import { docGetEntityGroupId } from "./docGetEntityGroupId";
import { docGetEntityId } from "./docGetEntityId";
import { appendDocRefReverseReferenceChildren } from "./docRefReverseReferenceChildren";
import { docEntityNodeExtractForeignKeyReferences, extractDocHashTableReferences } from "./docSchemaForeignKeyUtils";

export interface DocEntityNodeSource {
  getNode(doc: Doc, handle: DocEntityHandle): DocEntityNode | undefined;
}

export abstract class DocEntityNodeBase<T extends keyof DocEntityTypeToHandleType> implements DocEntityNode<T> {
  constructor(
    public readonly handle: DocEntityTypeToHandleType[T],
    protected readonly doc: Doc,
    protected docEntityNodeSource: DocEntityNodeSource,
  ) {}

  getHandle(): DocEntityTypeToHandleType[T] {
    return this.handle;
  }

  /**
   * `undefined` = not resolved yet; once resolved, the id string (or `null` when absent).
   */
  private idCached: string | null | undefined = undefined;
  getId(): string | undefined {
    if (this.idCached !== undefined) {
      return this.idCached ?? undefined;
    }

    const id = docGetEntityId(this.doc, this.handle);
    this.idCached = id ?? null;
    return id;
  }

  /**
   * `undefined` = not resolved yet; once resolved, the cached schema (or `null` when absent).
   */
  private docSchemaCached: Schema | null | undefined = undefined;
  getDocSchema(): Schema | undefined {
    if (this.docSchemaCached !== undefined) {
      return this.docSchemaCached ?? undefined;
    }
    const schema = docGetSchemaForEntity(this.doc, this.getHandle());
    this.docSchemaCached = schema ?? null;
    return schema;
  }

  private entityCached: DocEntityTypeToValueType[T] | undefined = undefined;
  getDocEntity(): DocEntityTypeToValueType[T] {
    return this.entityCached
      ? this.entityCached
      : (this.entityCached = docGetEntity(this.doc, this.handle) as DocEntityTypeToValueType[T]);
  }

  private handleHashCached: string | undefined = undefined;
  getHandleHash(): string {
    return this.handleHashCached ? this.handleHashCached : (this.handleHashCached = docEntityHandleHash(this.handle));
  }

  private nameCached: string | undefined;
  getName(): string {
    if (this.nameCached !== undefined) {
      return this.nameCached;
    }

    this.nameCached = docGetEntityName(this.doc, this.handle);
    return this.nameCached;
  }

  getTableName(): DocTableFieldNames | undefined {
    return docEntityTypeToDocTableName[this.handle.type];
  }

  getEntityTypePrettyName(): string {
    return docEntityTypePrettyName(this.doc, this.handle);
  }

  private reverseReferencesCached: DocEntityHandle[] | undefined = undefined;
  getReverseReferences(): DocEntityHandle[] {
    if (this.reverseReferencesCached) {
      return this.reverseReferencesCached;
    }

    const candidateEntityTypes = getDocEntityReverseReferenceCandidateTypes(this.getEntityType());
    if (!candidateEntityTypes.length) {
      return (this.reverseReferencesCached = []);
    }

    const index = getReverseReferenceIndex(this.doc, this.docEntityNodeSource);
    const rawSources = index.get(this.getHandleHash());
    if (!rawSources || rawSources.length === 0) {
      return (this.reverseReferencesCached = []);
    }

    const candidateTypeSet = new Set<DocEntityType>(candidateEntityTypes);
    const includeGroups = this.getConfig().isGroupable;

    const filtered: DocEntityHandle[] = [];
    for (const source of rawSources) {
      if (!candidateTypeSet.has(source.type as DocEntityType)) {
        continue;
      }
      if (!includeGroups && source.type === "GROUP") {
        continue;
      }
      filtered.push(source);
    }

    return (this.reverseReferencesCached = filtered);
  }

  private childHandleHashesCached: Set<string> | undefined = undefined;
  private getChildHandleHashSet(): Set<string> {
    if (this.childHandleHashesCached) {
      return this.childHandleHashesCached;
    }
    const set = new Set<string>();
    for (const child of this.getChildren()) {
      set.add(child.getHandleHash());
    }
    return (this.childHandleHashesCached = set);
  }

  hasChild(handle: DocEntityHandle): boolean {
    return this.getChildHandleHashSet().has(docEntityHandleHash(handle));
  }

  private referenceHandleHashesCached: Set<string> | undefined = undefined;
  private getReferenceHandleHashSet(): Set<string> {
    if (this.referenceHandleHashesCached) {
      return this.referenceHandleHashesCached;
    }
    const set = new Set<string>();
    for (const reference of this.getReferences()) {
      set.add(reference.getHandleHash());
    }
    return (this.referenceHandleHashesCached = set);
  }

  hasReference(handle: DocEntityHandle): boolean {
    return this.getReferenceHandleHashSet().has(docEntityHandleHash(handle));
  }

  private siblingHandleHashesCached: Set<string> | undefined = undefined;
  private getSiblingHandleHashSet(): Set<string> {
    if (this.siblingHandleHashesCached) {
      return this.siblingHandleHashesCached;
    }
    const set = new Set<string>();
    const siblings = this.getParent()?.getChildren() ?? [];
    for (const sibling of siblings) {
      set.add(sibling.getHandleHash());
    }
    return (this.siblingHandleHashesCached = set);
  }

  hasSibling(handle: DocEntityHandle): boolean {
    return this.getSiblingHandleHashSet().has(docEntityHandleHash(handle));
  }

  isChildOf(handle: DocEntityHandle): boolean {
    const parentHash = this.getParent()?.getHandleHash();
    if (!parentHash) {
      return false;
    }
    return parentHash === docEntityHandleHash(handle);
  }

  private childrenCached: DocEntityNode<DocEntityChildrenEntityType<T>>[] | undefined = undefined;
  getChildren(): DocEntityNode<DocEntityChildrenEntityType<T>>[] {
    if (this.childrenCached) {
      return this.childrenCached;
    }

    return (this.childrenCached = this.toNonNullEntitySet(docEntityGetChildren(this.doc, this.handle)).sort(
      (a, b) => a.getSortIndex() - b.getSortIndex(),
    ) as DocEntityNode<DocEntityChildrenEntityType<T>>[]);
  }

  private childrenOfTypeCached: { [childType: string]: DocEntityNode[] } = {};
  getChildrenOfType<T extends DocEntityType>(childType: T): DocEntityNode<T>[] {
    const cached = this.childrenOfTypeCached[childType];
    if (cached) {
      return cached as DocEntityNode<T>[];
    }

    const childrenTypes = docEntityTypeToChildTypes[this.getEntityType()] as readonly DocEntityType[];
    assert(childrenTypes.includes(childType));

    const handles = docEntityGetChildrenOfType(this.doc, childType, this.handle);
    const nodes = this.toNonNullEntitySet(handles);
    nodes.sort((a, b) => a.getSortIndex() - b.getSortIndex());

    return (this.childrenOfTypeCached[childType] = nodes as DocEntityNode<T>[]);
  }

  private recursiveChildrenCached: DocEntityNode[] | undefined = undefined;
  getRecursiveChildren(): DocEntityNode[] {
    if (this.recursiveChildrenCached) {
      return this.recursiveChildrenCached;
    }

    const recursiveChildren = this.getAllDependencies({
      sorted: true,
      traverseReferences: false,
      skipRoot: true,
    });

    return (this.recursiveChildrenCached = recursiveChildren);
  }

  /**
   * Deduplicates handles, resolves each to its {@link DocEntityNode}, and drops handles
   * that do not point to an existing entity.
   */
  private toNonNullEntitySet(handles: DocEntityHandle[]): DocEntityNode[] {
    const seen = new Set<string>();
    const nodes: DocEntityNode[] = [];

    for (const handle of handles) {
      const hash = docEntityHandleHash(handle);

      if (!handle || seen.has(hash)) {
        continue;
      }

      seen.add(hash);
      const node = this.docEntityNodeSource.getNode(this.doc, handle);

      if (!node?.getDocEntity()) {
        continue;
      }

      nodes.push(node);
    }
    return nodes;
  }

  private ungroupedChildrenCached: DocEntityNode[] | undefined = undefined;
  getChildrenIgnoringGroups(): DocEntityNode[] {
    if (this.ungroupedChildrenCached) {
      return this.ungroupedChildrenCached;
    }

    return (this.ungroupedChildrenCached = this.toNonNullEntitySet(
      docEntityGetChildrenIgnoringGroups(this.doc, this.handle),
    ));
  }

  /**
   * `undefined` = not resolved yet; once resolved, the parent node (or `null` when absent).
   */
  private parentCached:
    | DocEntityNode<Exclude<DocEntityTypeToParentHandleType[T], undefined>["type"]>
    | null
    | undefined = undefined;
  getParent(): DocEntityNode<Exclude<DocEntityTypeToParentHandleType[T], undefined>["type"]> | undefined {
    if (this.parentCached !== undefined) {
      return this.parentCached ?? undefined;
    }

    const parent = docEntityGetParent(this.doc, this.handle as DocEntityHandle);
    if (parent) {
      const parentNode = this.docEntityNodeSource.getNode(this.doc, parent) as
        | DocEntityNode<Exclude<DocEntityTypeToParentHandleType[T], undefined>["type"]>
        | undefined;
      this.parentCached = parentNode ?? null;
      return parentNode;
    }

    this.parentCached = null;
    return undefined;
  }

  /**
   * A group is really a kind of parent, but the entities in a group still point to their true
   * owner entity. This API allows you to get the group an entity is in, if possible.
   *
   * `undefined` = not resolved yet; once resolved, the group node (or `null` when absent).
   */
  private parentGroupCached: DocEntityNode<"GROUP"> | null | undefined = undefined;
  getParentGroup(): DocEntityNode<"GROUP"> | undefined {
    if (this.parentGroupCached !== undefined) {
      return this.parentGroupCached ?? undefined;
    }

    // Groups are not nested under other groups via entityGroups / SystemEntityGroups.
    // docGetEntityGroupId returns the handle id for GROUP handles, so skip that path.
    if (this.handle.type === "GROUP") {
      this.parentGroupCached = null;
      return undefined;
    }

    const groupId = docGetEntityGroupId(this.doc, this.handle);
    if (groupId) {
      const groupNode = this.docEntityNodeSource.getNode(this.doc, {
        type: "GROUP",
        id: groupId,
      }) as DocEntityNode<"GROUP"> | undefined;
      this.parentGroupCached = groupNode ?? null;
      return groupNode;
    }

    this.parentGroupCached = null;
    return undefined;
  }

  isInGroup() {
    return this.getParentGroup() !== undefined;
  }

  private fkReferencesCached: DocEntityReference[] | undefined = undefined;
  getForeignKeyReferences(): DocEntityReference[] {
    if (this.fkReferencesCached) {
      return this.fkReferencesCached;
    }
    const schemaReferences = docEntityNodeExtractForeignKeyReferences(this);
    const hashTableReferences = extractDocHashTableReferences(this.doc, this.getEntityType(), this.getHandleHash());
    const associationTableReferences = extractDocAssociationTableReferences(this.doc, this.handle);

    return (this.fkReferencesCached = [...schemaReferences, ...hashTableReferences, ...associationTableReferences]);
  }

  getTypeSpecificReferences(): DocEntityHandle[] {
    return [];
  }

  private referencesCached: DocEntityNode[] | undefined = undefined;
  getReferences(): DocEntityNode[] {
    if (this.referencesCached) {
      return this.referencesCached;
    }
    const fkReferences = this.getForeignKeyReferences();
    const typeSpecificReferences = this.getTypeSpecificReferences();

    if (!fkReferences.length && !typeSpecificReferences.length) {
      return (this.referencesCached = []);
    }

    const { valid } = validateDocEntityNodeReferences(this.doc, this, fkReferences);

    const references = [...valid, ...typeSpecificReferences];
    return (this.referencesCached = this.toNonNullEntitySet(references));
  }

  private reverseReferencesIgnoringGroupsCached: DocEntityHandle[] | undefined = undefined;
  getReverseReferencesIgnoringGroups(): DocEntityHandle[] {
    if (this.reverseReferencesIgnoringGroupsCached) {
      return this.reverseReferencesIgnoringGroupsCached;
    }

    const reverseRefs = this.getReverseReferences();
    const reverseRefsIgnoringGroups: DocEntityHandle[] = [];

    for (const ref of reverseRefs) {
      if (ref.type !== "GROUP") {
        reverseRefsIgnoringGroups.push(ref);
        continue;
      }

      /**
       * Collect the references for the group and keep only those that are not
       * children of the group.
       */
      const groupNode = this.docEntityNodeSource.getNode(this.doc, ref);
      assertIsNotUndefined(groupNode);

      for (const groupRefNode of groupNode.getReferences()) {
        const groupRef = groupRefNode.getHandle();
        if (!groupNode.hasChild(groupRef)) {
          reverseRefsIgnoringGroups.push(groupRef);
        }
      }
    }

    return (this.reverseReferencesIgnoringGroupsCached = reverseRefsIgnoringGroups);
  }

  getEntityType(): T {
    return this.handle.type as T;
  }

  getSortIndex(): number {
    const e = this.getDocEntity();
    if (e && "sortIndex" in e && typeof e.sortIndex === "number") {
      return e.sortIndex;
    }
    return 0;
  }

  private allDependenciesCached = new LRUObjectCache<DocEntityNodeGetAllDependenciesOptions, DocEntityNode[]>(
    3,
    allDependenciesOptionsEqual,
  );

  getAllDependencies(options: DocEntityNodeGetAllDependenciesOptions = {}): DocEntityNode[] {
    const cachedDependencies = this.allDependenciesCached.get(options);
    if (cachedDependencies) {
      return cachedDependencies;
    }

    const {
      skipEntitiesOfType = [],
      skipUndefinedEntities,
      sorted,
      traverseReferences = true,
      skipRoot = false,
    } = options;

    const skipEntitiesOfTypeSet = new Set(skipEntitiesOfType);

    if (!this.getDocEntity()) {
      return [];
    }

    const visited = new Set<string>();
    const toVisit: DocEntityNode[] = [this as DocEntityNode];
    const dependencies: DocEntityNode[] = [];

    if (!skipRoot) {
      dependencies.push(this as DocEntityNode);
    }

    const enqueueDependency = (entity: DocEntityNode): void => {
      const entityHash = entity.getHandleHash();

      // Skip if already visited or queued
      if (visited.has(entityHash)) {
        return;
      }

      // Skip undefined entities unless explicitly allowed
      if (!skipUndefinedEntities && !entity.getDocEntity()) {
        return;
      }

      // Skip filtered entity types
      if (skipEntitiesOfTypeSet.has(entity.getEntityType())) {
        return;
      }

      toVisit.push(entity);
    };

    while (toVisit.length > 0) {
      const current = toVisit.pop();
      if (!current) {
        continue;
      }

      const hash = current.getHandleHash();
      if (visited.has(hash)) {
        continue;
      }
      visited.add(hash);

      if (current !== this) {
        dependencies.push(current);
      }

      for (const entity of current.getChildren()) {
        enqueueDependency(entity);
      }

      if (traverseReferences) {
        for (const entity of current.getReferences()) {
          enqueueDependency(entity);
        }
      }
    }

    this.allDependenciesCached.add(options, dependencies);

    if (sorted) {
      return dependencies.sort((a, b) => a.getSortIndex() - b.getSortIndex());
    }

    return dependencies;
  }

  private allDependenciesTreeCached = new LRUObjectCache<
    DocEntityNodeGetAllDocRefDependenciesAsTreeOptions,
    DocEntityNodeTree
  >(3, allDocRefDependenciesTreeOptionsEqual);

  getAllDocRefDependenciesAsTree(options: DocEntityNodeGetAllDocRefDependenciesAsTreeOptions = {}): DocEntityNodeTree {
    const cachedTree = this.allDependenciesTreeCached.get(options);
    if (cachedTree) {
      return cachedTree;
    }

    const {
      skipEntitiesOfType = [],
      skipUndefinedEntities,
      traverseReferences = true,
      configFilterCriteria,
      traverseDependenciesCriteria,
      excludeReferenceFieldPaths,
    } = options;

    const resolvedConfigFilter = createConfigFilterPredicate(configFilterCriteria);
    const resolvedTraverseDependencies = createTraverseDependenciesPredicate(traverseDependenciesCriteria);

    const skipEntitiesOfTypeSet = new Set(skipEntitiesOfType);

    // Global cache for subtrees within this traversal - keyed by handle hash
    // This prevents recomputing shared dependencies when multiple paths lead to the same node
    const subtreeCache = new Map<string, DocEntityNodeTree>();

    const buildTree = (node: DocEntityNode, ancestorPath: Set<string>): DocEntityNodeTree => {
      const hash = node.getHandleHash();

      // Check if we've already computed this subtree (shared dependency optimization)
      const cached = subtreeCache.get(hash);
      if (cached) {
        return cached;
      }

      const fallback: DocEntityNodeTree = {
        node,
        handleHashesOfNodeAndDependencies: { [hash]: true },
        dependencies: [],
      };

      // Cycle detection - if this node is in our ancestor path, return fallback
      if (ancestorPath.has(hash)) {
        return fallback;
      }

      if (!skipUndefinedEntities && !node.getDocEntity()) {
        subtreeCache.set(hash, fallback);
        return fallback;
      }
      if (skipEntitiesOfTypeSet.has(node.getEntityType())) {
        subtreeCache.set(hash, fallback);
        return fallback;
      }
      if (resolvedConfigFilter && !resolvedConfigFilter(node.getConfig())) {
        subtreeCache.set(hash, fallback);
        return fallback;
      }

      // Add current node to ancestor path for cycle detection in children
      ancestorPath.add(hash);

      const childrenAndRefs: DocEntityNode[] = [];
      if (resolvedTraverseDependencies(node)) {
        for (const child of node.getChildren()) {
          childrenAndRefs.push(child);
        }
        if (traverseReferences) {
          for (const ref of getDocRefTraversalReferenceNodes(node, excludeReferenceFieldPaths)) {
            childrenAndRefs.push(ref);
          }
        }
      }

      appendDocRefReverseReferenceChildren(this.doc, node, this.docEntityNodeSource, childrenAndRefs);

      // Use a Set for deduplication instead of an object
      const seenChildren = new Set<string>();
      const children: DocEntityNodeTree[] = [];

      for (const child of childrenAndRefs) {
        const childHash = child.getHandleHash();

        // Skip duplicates and cycles
        if (seenChildren.has(childHash) || ancestorPath.has(childHash)) {
          continue;
        }
        seenChildren.add(childHash);

        // Skip filtered entity types
        if (skipEntitiesOfTypeSet.has(child.getEntityType())) {
          continue;
        }
        if (resolvedConfigFilter && !resolvedConfigFilter(child.getConfig())) {
          continue;
        }

        children.push(buildTree(child, ancestorPath));
      }

      // Remove current node from ancestor path after processing children
      ancestorPath.delete(hash);

      const sortedChildren = children.sort((a, b) => a.node.getSortIndex() - b.node.getSortIndex());

      // Build hash set from children's hash sets
      const handleHashSet: { [docEntityHandleHash: string]: true } = { [hash]: true };
      for (const childTree of sortedChildren) {
        const childMap = childTree.handleHashesOfNodeAndDependencies;
        for (const childHash in childMap) {
          if (childMap[childHash]) {
            handleHashSet[childHash] = true;
          }
        }
      }

      const result: DocEntityNodeTree = {
        node,
        handleHashesOfNodeAndDependencies: handleHashSet,
        dependencies: sortedChildren,
      };

      // Cache the result for reuse by other paths
      subtreeCache.set(hash, result);
      return result;
    };

    const tree = buildTree(this as DocEntityNode, new Set());
    this.allDependenciesTreeCached.add(options, tree);
    return tree;
  }

  private configCached: DocEntityConfig | undefined = undefined;
  getConfig(): DocEntityConfig {
    if (this.configCached) {
      return this.configCached;
    }

    return (this.configCached = docEntityGetConfig(this.doc, this.handle, this.docEntityNodeSource));
  }

  private sourceAssociations: DocEntityNode[] | undefined = undefined;
  getSourceAssociations(): DocEntityNode[] {
    if (this.sourceAssociations) {
      return this.sourceAssociations;
    }

    return (this.sourceAssociations = this.toNonNullEntitySet(docGetSourceAssociatedEntities(this.doc, this.handle)));
  }

  private targetAssociations: DocEntityNode[] | undefined = undefined;
  getTargetAssociations(): DocEntityNode[] {
    if (this.targetAssociations) {
      return this.targetAssociations;
    }

    return (this.targetAssociations = this.toNonNullEntitySet(docGetTargetAssociatedEntities(this.doc, this.handle)));
  }
}

/**
 * Per-doc reverse-reference index: `targetHandleHash → source handles`.
 *
 * Built lazily on the first getReverseReferences call for any node
 * in the doc, then shared across every other node in that same doc.
 *
 * Keyed weakly on Doc, so cache entries become eligible for collection as soon as the
 * Doc has no other strong references in the system. We never need to clear this cache
 * manually, even across long-running sessions that churn many doc snapshots.
 *
 * Note: this is keyed on Doc alone (not `(Doc, nodeSource)`), which is safe because
 * {@link DocEntityNodeRegistry} is a singleton and the only `DocEntityNodeSource`.
 */
const reverseReferenceIndexCache = new WeakMap<Doc, Map<string, DocEntityHandle[]>>();

function getReverseReferenceIndex(doc: Doc, nodeSource: DocEntityNodeSource): Map<string, DocEntityHandle[]> {
  const existing = reverseReferenceIndexCache.get(doc);
  if (existing) {
    return existing;
  }

  const index = new Map<string, DocEntityHandle[]>();
  /**
   * Register the (empty) map in the cache BEFORE iterating. This guards against subclasses
   * that call getReverseReferences from within getTypeSpecificReferences, which would cause
   * a stack overflow. NOTE: a re-entrant caller will see a partial index (only nodes visited
   * so far), which is acceptable — the alternative is unbounded recursion.
   */
  reverseReferenceIndexCache.set(doc, index);

  /**
   * Iterate every entity once and record each of its outgoing references as an incoming
   * reverse reference on the target side.
   */
  docForEachEntityHandle(doc, (sourceHandle) => {
    const sourceNode = nodeSource.getNode(doc, sourceHandle);
    if (!sourceNode) {
      return;
    }

    for (const referencedNode of sourceNode.getReferences()) {
      const targetHash = referencedNode.getHandleHash();
      let sources = index.get(targetHash);
      if (!sources) {
        sources = [];
        index.set(targetHash, sources);
      }
      sources.push(sourceHandle);
    }
  });

  return index;
}

function createConfigFilterPredicate(
  criteria?: DocEntityNodeConfigFilterCriteria,
): ((config: DocEntityConfig) => boolean) | undefined {
  if (!criteria) {
    return undefined;
  }

  const { excludeSystemEntities } = criteria;

  if (!excludeSystemEntities) {
    return undefined;
  }

  return (config: DocEntityConfig) => !config.isSystemEntity;
}

const alwaysTraverseDependencies = () => true;

function createTraverseDependenciesPredicate(
  criteria?: DocEntityNodeTraverseDependenciesCriteria,
): (node: DocEntityNode) => boolean {
  if (!criteria) {
    return alwaysTraverseDependencies;
  }

  const { skipEntityTypes } = criteria;
  if (!skipEntityTypes || skipEntityTypes.length === 0) {
    return alwaysTraverseDependencies;
  }

  const skippedTypes = new Set(skipEntityTypes);
  return (node: DocEntityNode) => !skippedTypes.has(node.getEntityType());
}

/**
 * Structural equality for {@link DocEntityNodeGetAllDependenciesOptions}. Avoids the cost of
 * recursive deepequal on every cache probe; all known fields are scalars or shallow arrays.
 */
function allDependenciesOptionsEqual(
  a: DocEntityNodeGetAllDependenciesOptions,
  b: DocEntityNodeGetAllDependenciesOptions,
): boolean {
  if (a === b) {
    return true;
  }
  return (
    a.skipUndefinedEntities === b.skipUndefinedEntities &&
    a.sorted === b.sorted &&
    a.traverseReferences === b.traverseReferences &&
    a.skipRoot === b.skipRoot &&
    arrayShallowEquals(a.skipEntitiesOfType, b.skipEntitiesOfType)
  );
}

function configFilterCriteriaEqual(
  a: DocEntityNodeConfigFilterCriteria | undefined,
  b: DocEntityNodeConfigFilterCriteria | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.excludeSystemEntities === b.excludeSystemEntities;
}

function traverseDependenciesCriteriaEqual(
  a: DocEntityNodeTraverseDependenciesCriteria | undefined,
  b: DocEntityNodeTraverseDependenciesCriteria | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return arrayShallowEquals(a.skipEntityTypes, b.skipEntityTypes);
}

/**
 * Structural equality for {@link DocEntityNodeGetAllDocRefDependenciesAsTreeOptions}. Replaces a
 * `stableHash` comparison that would serialize both options objects on every cache probe.
 */
function allDocRefDependenciesTreeOptionsEqual(
  a: DocEntityNodeGetAllDocRefDependenciesAsTreeOptions,
  b: DocEntityNodeGetAllDocRefDependenciesAsTreeOptions,
): boolean {
  if (a === b) {
    return true;
  }
  return (
    allDependenciesOptionsEqual(a, b) &&
    configFilterCriteriaEqual(a.configFilterCriteria, b.configFilterCriteria) &&
    traverseDependenciesCriteriaEqual(a.traverseDependenciesCriteria, b.traverseDependenciesCriteria) &&
    a.excludeReferenceFieldPaths === b.excludeReferenceFieldPaths
  );
}
